import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipPayload } from 'recharts/types/state/tooltipSlice';

import {
  barChartAxisTickStyle,
  barChartCenteredBoxSx,
  barChartDollarLabelStyle,
  barChartGridStroke,
  barChartTooltipCursorSx,
} from './chart.styles';
import { computeBarSize, formatYAxisTick } from './chartUtils';

export interface BarDef {
  dataKey: string;
  color: string;
  label: string;
  showDollarLabel?: boolean;
}

export interface BarChartDatum {
  label: string;
  key: string;
  [dataKey: string]: string | number;
}

interface Props {
  data: BarChartDatum[];
  bars: BarDef[];
  height?: number;
  loading?: boolean;
  tooltipContent?: (payload: TooltipPayload | undefined, label: string) => React.ReactNode;
  onBarClick?: (datum: BarChartDatum) => void;
}

const BarChartCard: React.FC<Props> = ({
  data,
  bars,
  height = 260,
  loading = false,
  tooltipContent,
  onBarClick,
}) => {
  if (loading) {
    return (
      <Box sx={{ ...barChartCenteredBoxSx, height }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!data.length) {
    return (
      <Box sx={{ ...barChartCenteredBoxSx, height }}>
        <Typography variant="body2" color="text.secondary">
          No data for the selected period.
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height} style={{ outline: 'none' }}>
      <style>{`.recharts-wrapper, .recharts-wrapper svg, .recharts-wrapper *:focus { outline: none !important; }`}</style>
      <BarChart
        data={data}
        barSize={computeBarSize(data.length)}
        barCategoryGap="20%"
        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
        style={{ outline: 'none', cursor: onBarClick ? 'pointer' : 'default' }}
      >
        <CartesianGrid vertical={false} stroke={barChartGridStroke} strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tick={barChartAxisTickStyle}
          tickLine={false}
          axisLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tickFormatter={formatYAxisTick}
          tick={barChartAxisTickStyle}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        <Tooltip
          cursor={barChartTooltipCursorSx}
          content={
            tooltipContent
              ? ({ payload, label }) => tooltipContent(payload, String(label ?? ''))
              : undefined
          }
        />
        {bars.map((bar) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            stackId="stack"
            fill={bar.color}
            radius={[0, 0, 0, 0]}
            onClick={(barData: unknown) => {
              if (onBarClick) onBarClick((barData as { payload?: BarChartDatum })?.payload ?? (barData as BarChartDatum));
            }}
          >
            {data.map((datum) => {
              // Find the topmost bar with a non-zero value for this datum
              const topBarKey = [...bars].reverse().find((b) => Number(datum[b.dataKey]) > 0)?.dataKey;
              const isTop = bar.dataKey === topBarKey;
              return (
                <Cell
                  key={datum.key}
                  fill={bar.color}
                  {...({ radius: isTop ? [4, 4, 0, 0] : [0, 0, 0, 0] } as object)}
                />
              );
            })}
            {bar.showDollarLabel && (
              <LabelList
                dataKey={bar.dataKey}
                position="center"
                formatter={() => '$'}
                style={barChartDollarLabelStyle}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BarChartCard;
